/**
 * Invoice Types as defined by DGI Burkina Faso
 * Reference: Spécifications Techniques SECeF v2.0
 *
 * Standard invoices: FV, FA
 * Simplified invoices: FT
 * Credit notes (avoirs): EV, EA, ET
 */
/**
 * Default amount, in franc CFA, up to which `suggestInvoiceType` proposes FT
 * or ET for an operation without client identification. No text sets it and
 * no rule refuses an invoice on it: FT and ET carry no amount ceiling.
 */
export const CLIENT_ID_THRESHOLD = 50_000;
/**
 * Invoice types as defined by DGI
 */
export const INVOICE_TYPES = {
    /**
     * Facture de Vente - Standard sales invoice
     * No amount limit, requires client identification
     */
    FV: {
        code: "FV",
        label: "Facture de Vente",
        requiresClientId: true,
        isCreditNote: false,
        minAmount: 0,
        description: "Facture standard avec identification du client obligatoire",
    },
    /**
     * Facture d'Acompte - Advance/deposit invoice, no amount ceiling
     */
    FT: {
        code: "FT",
        label: "Facture d'Acompte",
        requiresClientId: false,
        isCreditNote: false,
        minAmount: 0,
        description: "Facture d'acompte ou d'avance",
    },
    /**
     * Facture d'Avoir - Standard credit note
     * Must reference original FV invoice
     */
    FA: {
        code: "FA",
        label: "Facture d'Avoir",
        requiresClientId: true,
        isCreditNote: true,
        minAmount: 0,
        description: "Avoir sur facture de vente (retour, remise, erreur)",
    },
    /**
     * Exportation Vente - Export invoice
     * For goods/services destined for export
     */
    EV: {
        code: "EV",
        label: "Exportation Vente",
        requiresClientId: true,
        isCreditNote: false,
        minAmount: 0,
        description: "Facture d'exportation de biens ou services",
    },
    /**
     * Facture Export Acompte - Export advance/deposit invoice, no amount ceiling
     */
    ET: {
        code: "ET",
        label: "Facture Export Acompte",
        requiresClientId: false,
        isCreditNote: false,
        minAmount: 0,
        description: "Facture d'acompte ou d'avance à l'exportation",
    },
    /**
     * Exportation Avoir - Export credit note
     * Must reference original EV invoice
     */
    EA: {
        code: "EA",
        label: "Exportation Avoir",
        requiresClientId: true,
        isCreditNote: true,
        minAmount: 0,
        description: "Avoir sur facture d'exportation",
    },
};
/** All invoice type codes, for z.enum() and other exhaustive checks. */
export const INVOICE_TYPE_CODES = Object.keys(INVOICE_TYPES);
/** Credit note (avoir) invoice type codes: FA, EA. */
export const CREDIT_NOTE_TYPE_CODES = Object.values(INVOICE_TYPES)
    .filter((t) => t.isCreditNote)
    .map((t) => t.code);
/** Invoice types a POS sale may issue: every type but the credit notes. */
export const POS_INVOICE_TYPE_CODES = INVOICE_TYPE_CODES.filter((code) => !CREDIT_NOTE_TYPE_CODES.includes(code));
/**
 * All export-flow invoice type codes: EV, ET, EA (sales, advances and their
 * credit note). Wider than getExportTypes(), which excludes the credit note.
 */
export const EXPORT_TYPE_CODES = Object.values(INVOICE_TYPES)
    .filter((t) => t.code.startsWith("E"))
    .map((t) => t.code);
/**
 * Get invoice type by code
 */
export function getInvoiceType(code) {
    return INVOICE_TYPES[code];
}
/**
 * Get all credit note types
 */
export function getCreditNoteTypes() {
    return Object.values(INVOICE_TYPES).filter((t) => t.isCreditNote);
}
/**
 * Get all export invoice types
 */
export function getExportTypes() {
    return Object.values(INVOICE_TYPES).filter((t) => t.code.startsWith("E") && !t.isCreditNote);
}
/**
 * Get the corresponding credit note type for an invoice type
 */
export function getCreditNoteTypeFor(invoiceCode) {
    switch (invoiceCode) {
        case "FV":
        case "FT":
            return "FA";
        case "EV":
        case "ET":
            return "EA";
        default:
            return null; // Credit notes don't have credit notes
    }
}
/**
 * Proposes an invoice type from the amount and the context: FT or ET for an
 * operation up to `threshold` without client identification, FV or EV
 * otherwise. A proposal only; it refuses nothing.
 */
export function suggestInvoiceType(amount, isExport, hasClientId, threshold = CLIENT_ID_THRESHOLD) {
    if (isExport) {
        if (amount <= threshold && !hasClientId) {
            return "ET";
        }
        return "EV";
    }
    if (amount <= threshold && !hasClientId) {
        return "FT";
    }
    return "FV";
}
//# sourceMappingURL=invoice-types.js.map